import React from "react";
import { Box, Card, Group, Paper, SimpleGrid, Stack, Table, Text } from "@mantine/core";
import {
  difficultyLadderForTask,
  DyscalculiaDifficultyStatus,
  DYSCALCULIA_DIFFICULTY_LEVELS,
  DYSCALCULIA_DIFFICULTY_STATUS_PRESENTATION,
} from "@constants/testConstants/testSpecificConstants/dyscalculiaDifficultyConstants";
import { useTranslation } from "react-i18next";
import type { TFunction } from "i18next";
import {
  DyscalculiaDifficultyLevel,
  DyscalculiaResultItem,
  DyscalculiaTaskResult,
  DyscalculiaTaskType,
  DotEnumerationResult,
  MagnitudeComparisonResult,
  StroopResult,
} from "types/testTypes";

export const TASK_LABEL_KEYS: Record<DyscalculiaTaskType, string> = {
  magnitudeComparisonSymbolic: "magnitudeComparisonSymbolic",
  magnitudeComparisonNonSymbolic: "magnitudeComparisonNonSymbolic",
  stroop: "stroop",
  dotEnumeration: "dotEnumeration",
  numberLine: "numberLine",
};

export const resolveTaskLabelKey = (task: DyscalculiaTaskResult) => {
  if (task.taskType === "magnitudeComparisonSymbolic") {
    if (task.taskId?.includes("fraction_compare")) return "fractionComparison";
    if (task.taskId?.includes("negative")) return "negativeNumberComparison";
  }

  return TASK_LABEL_KEYS[task.taskType];
};

export const formatSeconds = (value?: number | null) =>
  typeof value === "number" ? `${value.toFixed(2)} s` : "—";

const booleanLabel = (value?: boolean | null, trueLabel = "", falseLabel = "") =>
  value == null ? "—" : value ? trueLabel : falseLabel;

export const getStats = (results?: DyscalculiaResultItem[]) => {
  const safeResults = Array.isArray(results) ? results : [];
  const count = safeResults.length;
  const correct = safeResults.filter((item) => {
    return (
      "wasCorrect" in item &&
      typeof (item as any).wasCorrect === "boolean" &&
      (item as MagnitudeComparisonResult).wasCorrect
    );
  }).length;
  const avgRt =
    count > 0
      ? safeResults.reduce((acc, item) => {
          if (
            "reactionTimeSeconds" in item &&
            typeof (item as any).reactionTimeSeconds === "number"
          ) {
            return acc + (item as MagnitudeComparisonResult).reactionTimeSeconds;
          }
          return acc;
        }, 0) / count
      : null;
  const accuracy = count > 0 ? Math.round((correct / count) * 100) : null;

  return { count, correct, accuracy, avgRt };
};

export const accuracyColor = (accuracy: number | null) => {
  if (accuracy === null) return "gray";
  if (accuracy >= 80) return "green";
  if (accuracy >= 50) return "yellow";
  return "red";
};

const renderComparison = (results: MagnitudeComparisonResult[], t: TFunction) => (
  <Table withRowBorders={false} verticalSpacing="xs" horizontalSpacing="xs">
    <Table.Thead>
      <Table.Tr>
        <Table.Th>{t("stimuli")}</Table.Th>
        <Table.Th>{t("correct")}</Table.Th>
        <Table.Th>{t("reaction")}</Table.Th>
      </Table.Tr>
    </Table.Thead>
    <Table.Tbody>
      {results.map((item, index) => (
        <Table.Tr key={`mc-${index}`}>
          <Table.Td>
            <Text size="sm">
              {item.leftQuantity ?? "—"} | {item.rightQuantity ?? "—"}
            </Text>
          </Table.Td>
          <Table.Td>{booleanLabel(item.wasCorrect, t("yes"), t("no"))}</Table.Td>
          <Table.Td>{formatSeconds(item.reactionTimeSeconds)}</Table.Td>
        </Table.Tr>
      ))}
    </Table.Tbody>
  </Table>
);

const renderStroop = (results: StroopResult[], t: TFunction) => (
  <Table withRowBorders={false} verticalSpacing="xs" horizontalSpacing="xs">
    <Table.Thead>
      <Table.Tr>
        <Table.Th>{t("stimuli")}</Table.Th>
        <Table.Th>{t("correct")}</Table.Th>
        <Table.Th>{t("reaction")}</Table.Th>
      </Table.Tr>
    </Table.Thead>
    <Table.Tbody>
      {results.map((item, index) => (
        <Table.Tr key={`stroop-${index}`}>
          <Table.Td>
            <Group gap={6} wrap="nowrap">
              <Text size="sm">{item.leftNumber ?? "—"}</Text>
              <Text size="sm" c="dimmed">
                |
              </Text>
              <Text size="sm">{item.rightNumber ?? "—"}</Text>
            </Group>
          </Table.Td>
          <Table.Td>{booleanLabel(item.wasCorrect, t("yes"), t("no"))}</Table.Td>
          <Table.Td>{formatSeconds(item.reactionTimeSeconds)}</Table.Td>
        </Table.Tr>
      ))}
    </Table.Tbody>
  </Table>
);

const renderDotEnumeration = (results: DotEnumerationResult[], t: TFunction) => (
  <Table withRowBorders={false} verticalSpacing="xs" horizontalSpacing="xs">
    <Table.Thead>
      <Table.Tr>
        <Table.Th>{t("target")}</Table.Th>
        <Table.Th>{t("selected")}</Table.Th>
        <Table.Th>{t("correct")}</Table.Th>
        <Table.Th>{t("display")}</Table.Th>
        <Table.Th>{t("reaction")}</Table.Th>
      </Table.Tr>
    </Table.Thead>
    <Table.Tbody>
      {results.map((item, index) => (
        <Table.Tr key={`dot-${index}`}>
          <Table.Td>{item.targetCount ?? "—"}</Table.Td>
          <Table.Td>{item.selectedValue ?? "—"}</Table.Td>
          <Table.Td>{booleanLabel(item.wasCorrect, t("yes"), t("no"))}</Table.Td>
          <Table.Td>{formatSeconds(item.displayDurationSeconds)}</Table.Td>
          <Table.Td>{formatSeconds(item.reactionTimeSeconds)}</Table.Td>
        </Table.Tr>
      ))}
    </Table.Tbody>
  </Table>
);

const renderNumberLine = (results: any[], t: TFunction) => {
  const fmtNum = (n: number) => {
    const s = n.toFixed(1);
    return s.endsWith(".0") ? s.slice(0, -2) : s;
  };

  const computePercentFromRange = (
    value: number | null | undefined,
    start: number | null | undefined,
    end: number | null | undefined,
  ) => {
    if (value == null || start == null || end == null) return null;
    const range = end - start;
    if (range === 0) return null;
    return ((value - start) / range) * 100;
  };

  return (
    <Table withRowBorders={false} verticalSpacing="xs" horizontalSpacing="xs">
      <Table.Thead>
        <Table.Tr>
          <Table.Th>{t("targetPercentage")}</Table.Th>
          <Table.Th>{t("selectedPercentage")}</Table.Th>
          <Table.Th>{t("Error")}</Table.Th>
          <Table.Th>{t("reaction")}</Table.Th>
        </Table.Tr>
      </Table.Thead>
      <Table.Tbody>
        {results.map((item, index) => {
          const start = item.startValue;
          const end = item.endValue;

          const targetPctRaw =
            item.targetPercentage != null
              ? Number(item.targetPercentage)
              : computePercentFromRange(item.targetValue, start, end);

          const selectedPctRaw =
            item.selectedPercentage != null
              ? Number(item.selectedPercentage)
              : computePercentFromRange(item.selectedValue, start, end);

          let errorDisplay: string | null = null;
          if (item.errorAbsolute != null) {
            const err = Number(item.errorAbsolute);
            if (start != null && end != null && end !== start) {
              const errPct = (err / (end - start)) * 100;
              errorDisplay = `${fmtNum(errPct)}%`;
            } else if (err > 0 && err <= 1) {
              // likely a 0..1 value – show as percent
              errorDisplay = `${fmtNum(err * 100)}%`;
            } else {
              errorDisplay = `${err}`;
            }
          }

          const targetStr = targetPctRaw != null ? `${fmtNum(targetPctRaw)}%` : "—";
          const selectedStr = selectedPctRaw != null ? `${fmtNum(selectedPctRaw)}%` : "—";

          return (
            <Table.Tr key={`line-${index}`}>
              <Table.Td>{targetStr}</Table.Td>
              <Table.Td>{selectedStr}</Table.Td>
              <Table.Td>{errorDisplay ?? "—"}</Table.Td>
              <Table.Td>{formatSeconds(item.reactionTimeSeconds)}</Table.Td>
            </Table.Tr>
          );
        })}
      </Table.Tbody>
    </Table>
  );
};

export const renderTaskContent = (task: DyscalculiaTaskResult, t: TFunction) => {
  if (!Array.isArray(task.results) || task.results.length === 0) {
    return (
      <Text size="sm" c="dimmed">
        {t("noTrials")}
      </Text>
    );
  }

  if (
    task.taskType === "magnitudeComparisonSymbolic" ||
    task.taskType === "magnitudeComparisonNonSymbolic"
  ) {
    return renderComparison(task.results as MagnitudeComparisonResult[], t);
  }

  if (task.taskType === "stroop") {
    return renderStroop(task.results as StroopResult[], t);
  }

  if (task.taskType === "numberLine") {
    return renderNumberLine(task.results as any[], t);
  }

  if (task.taskType === "dotEnumeration") {
    return renderDotEnumeration(task.results as DotEnumerationResult[], t);
  }

  return (
    <Text size="sm" c="dimmed">
      {t("unsupported")}
    </Text>
  );
};

const DifficultyLadder: React.FC<{
  task: DyscalculiaTaskResult;
  trialLevels?: (DyscalculiaDifficultyLevel | null)[];
}> = ({ task, trialLevels }) => {
  const ladder = difficultyLadderForTask(task);
  if (!ladder) return null;

  const statusForLevel = (level: DyscalculiaDifficultyLevel): DyscalculiaDifficultyStatus => {
    const resultsAtLevel = (trialLevels ?? []).flatMap((trialLevel, index) => {
      if (trialLevel !== level) return [];
      const result = task.results?.[index];
      return result && "wasCorrect" in result && typeof result.wasCorrect === "boolean"
        ? [result.wasCorrect]
        : [];
    });

    if (resultsAtLevel.length === 0) return "notGiven";
    const correct = resultsAtLevel.filter(Boolean).length;
    return correct / resultsAtLevel.length >= 0.8 ? "highAccuracy" : "lowAccuracy";
  };

  return (
    <Stack gap={6}>
      <Text size="xs" c="dimmed">
        {ladder.effect}
      </Text>
      <SimpleGrid cols={4} spacing={6}>
        {DYSCALCULIA_DIFFICULTY_LEVELS.map((level) => {
          const status = statusForLevel(level);
          const presentation = DYSCALCULIA_DIFFICULTY_STATUS_PRESENTATION[status];
          return (
            <Paper key={level} px={4} py={6} radius="sm" withBorder>
              <Group gap={5} justify="center" wrap="nowrap">
                <Box
                  w={8}
                  h={8}
                  bg={`var(--mantine-color-${presentation.color}-6)`}
                  style={{ borderRadius: "50%", flexShrink: 0 }}
                />
                <Text size="xs" fw={700} ta="center">
                  L{level}
                </Text>
              </Group>
            </Paper>
          );
        })}
      </SimpleGrid>
    </Stack>
  );
};

type DyscalculiaTaskCardProps = {
  task: DyscalculiaTaskResult;
  subtitle?: string;
  description?: string;
  showTrials?: boolean;
  trialLevels?: (DyscalculiaDifficultyLevel | null)[];
};

const DyscalculiaTaskCard: React.FC<DyscalculiaTaskCardProps> = ({
  task,
  subtitle,
  description,
  showTrials = true,
  trialLevels,
}) => {
  const { t } = useTranslation();
  const taskLabelKey = resolveTaskLabelKey(task);
  const taskLabel = taskLabelKey ? t(taskLabelKey) : task.taskType;
  const { accuracy, avgRt, count } = getStats(task.results);
  const accuracyColorName = accuracyColor(accuracy);
  const accuracyBg = `var(--mantine-color-${accuracyColorName}-1)`;
  const accuracyText = `var(--mantine-color-${accuracyColorName}-9)`;

  return (
    <Card withBorder radius="md" p="md" shadow="xs">
      <Stack gap="sm">
        <Group justify="space-between" align="flex-start">
          <Stack gap={4}>
            <Text fw={600}>{taskLabel}</Text>
            {subtitle && (
              <Text size="sm" c="dimmed">
                {subtitle}
              </Text>
            )}
            {description && (
              <Text size="sm" c="dimmed">
                {description}
              </Text>
            )}
          </Stack>
        </Group>

        <DifficultyLadder task={task} trialLevels={trialLevels} />

        <Group gap="xs" wrap="wrap">
          <Paper px="sm" py={6} radius="sm" bg={accuracyBg} c={accuracyText}>
            <Text size="xs" fw={600} mb={2}>
              {t("accuracy")}
            </Text>
            <Text size="sm">{accuracy === null ? "—" : `${accuracy}%`}</Text>
          </Paper>
          {showTrials && (
            <Paper px="sm" py={6} radius="sm" withBorder>
              <Text size="xs" fw={600} mb={2}>
                {t("trials")}
              </Text>
              <Text size="sm">{count}</Text>
            </Paper>
          )}
          <Paper px="sm" py={6} radius="sm" withBorder>
            <Text size="xs" fw={600} mb={2}>
              {t("avgReactionTime")}
            </Text>
            <Text size="sm">{avgRt === null ? "—" : formatSeconds(avgRt)}</Text>
          </Paper>
        </Group>

        {renderTaskContent(task, t)}
      </Stack>
    </Card>
  );
};

export default DyscalculiaTaskCard;
